/**
 * Serviço de importação de clientes do IXC.
 * Lista clientes na API IXC e dispara importação por etapas no backend.
 */
import { api } from './api';

export interface IxcClienteListItem {
  id: number | string;
  nome: string;
  status: string;
}

export interface IxcImportProgressItem {
  id_ixc: number | string;
  nome: string;
  etapa: string;
  status: 'processando' | 'sucesso' | 'erro';
  erro?: string;
  detalhes?: {
    cliente_id?: number;
    contratos_importados?: number;
    servicos_importados?: number;
    faturas_importadas?: number;
    pix_atualizados?: number;
  };
}

export interface IxcListClientesParams {
  baseUrl: string;
  /** Credencial no formato id:token (Basic auth) */
  token: string;
  status?: 'ativos' | 'inativos' | 'todos';
}

export interface IxcImportarParams {
  baseUrl: string;
  token: string;
  idsClientesIxc: (number | string)[];
  escritorio_id: number;
}

export interface IxcImportarUnicoParams {
  baseUrl: string;
  token: string;
  idClienteIxc: number | string;
  escritorio_id: number;
}

export interface IxcPlanoListItem {
  id: number | string;
  grupo: string;
  download: string;
  upload: string;
  valor_produto: string;
}

export interface IxcImportPlanosParams {
  baseUrl: string;
  token: string;
  idsPlanosIxc: (number | string)[];
  escritorio_id: number;
}

export interface IxcImportPlanosResult {
  success: number;
  errors: number;
  results: { id_ixc: string; nome: string; status: 'sucesso' | 'erro'; erro?: string }[];
}

class IxcImportService {
  async listClientes(params: IxcListClientesParams): Promise<IxcClienteListItem[]> {
    const response = await api.post('/import/ixc/clientes', {
      baseUrl: params.baseUrl,
      token: params.token,
      status: params.status,
    }) as { success: boolean; data: IxcClienteListItem[] };
    if (!response.success || !response.data) {
      throw new Error('Resposta inválida ao listar clientes IXC');
    }
    return response.data;
  }

  async importar(params: IxcImportarParams): Promise<IxcImportProgressItem[]> {
    const response = await api.post('/import/ixc/importar', {
      baseUrl: params.baseUrl,
      token: params.token,
      idsClientesIxc: params.idsClientesIxc,
      escritorio_id: params.escritorio_id,
    }) as { success: boolean; data: IxcImportProgressItem[] };
    if (!response.success) {
      throw new Error('Falha na importação IXC');
    }
    return response.data || [];
  }

  async importarUnico(params: IxcImportarUnicoParams): Promise<IxcImportProgressItem> {
    const response = await api.post('/import/ixc/importar-unico', {
      baseUrl: params.baseUrl,
      token: params.token,
      idClienteIxc: params.idClienteIxc,
      escritorio_id: params.escritorio_id,
    }) as { success: boolean; data: IxcImportProgressItem };
    if (!response.success) {
      throw new Error('Falha na importação do cliente IXC');
    }
    return response.data;
  }

  async listPlanos(params: IxcListClientesParams): Promise<IxcPlanoListItem[]> {
    const response = await api.post('/import/ixc/planos', {
      baseUrl: params.baseUrl,
      token: params.token,
    }) as { success: boolean; data: IxcPlanoListItem[] };
    if (!response.success || !response.data) {
      throw new Error('Resposta inválida ao listar planos IXC');
    }
    return response.data;
  }

  async importarPlanos(params: IxcImportPlanosParams): Promise<IxcImportPlanosResult> {
    const response = await api.post('/import/ixc/planos/importar', {
      baseUrl: params.baseUrl,
      token: params.token,
      idsPlanosIxc: params.idsPlanosIxc,
      escritorio_id: params.escritorio_id,
    }) as { success: boolean; data: IxcImportPlanosResult };
    if (!response.success) {
      throw new Error('Falha na importação de planos IXC');
    }
    return response.data;
  }

  async listClientesComImportacao(status: 'ativos' | 'inativos' | 'todos' = 'ativos'): Promise<any[]> {
    const response = await api.get('/import/ixc/clientes/importacao', {
      params: { status }
    }) as { success: boolean; data: any[] };
    if (!response.success || !response.data) {
      throw new Error('Falha ao listar clientes com ID de importação');
    }
    return response.data;
  }

  async importarDocumentosClienteUnico(params: {
    baseUrl: string;
    token: string;
    clienteId: number;
  }): Promise<{ status: 'sucesso' | 'erro'; erro?: string; arquivos_importados: number }> {
    const response = await api.post('/import/ixc/clientes/importar-documentos-unico', params) as { success: boolean; data: { status: 'sucesso' | 'erro'; erro?: string; arquivos_importados: number } };
    if (!response.success) {
      throw new Error('Falha na importação dos documentos do cliente');
    }
    return response.data;
  }

  async listContratosComImportacao(status: 'ativos' | 'cancelados' | 'todos' = 'ativos'): Promise<any[]> {
    const response = await api.get('/import/ixc/contratos/importacao', {
      params: { status }
    }) as { success: boolean; data: any[] };
    if (!response.success || !response.data) {
      throw new Error('Falha ao listar contratos com ID de importação');
    }
    return response.data;
  }

  async importarContratoPdfUnico(params: {
    baseUrl: string;
    token: string;
    contratoId: number;
  }): Promise<{ status: 'sucesso' | 'erro'; erro?: string; contrato?: string }> {
    const response = await api.post('/import/ixc/contratos/importar-pdf-unico', params) as { success: boolean; data: { status: 'sucesso' | 'erro'; erro?: string; contrato?: string } };
    if (!response.success) {
      throw new Error('Falha na importação do PDF do contrato');
    }
    return response.data;
  }

}

export const ixcImportService = new IxcImportService();
